import React, { useEffect, useState, useMemo } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Button, Box, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import Paging from './Paging';
import { fetchWithToken } from "../api/apiutils";

const SearchCustomersTable = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const [currentItem, setCurrentItem] = useState(null);
  const [aliases, setAliases] = useState('');

  const fetchData = async (page) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithToken(`/api/customers/oked?page=${page}&pageLimit=${rowsPerPage}`);
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const result = await response.json();
      setData(result.data);
      setTotalRecords(result.all);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalRecords / rowsPerPage);
  }, [totalRecords]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOpenAddDialog = (item) => {
    setCurrentItem(item);
    setAliases('');
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (item) => {
    setCurrentItem(item);
    setAliases(item.aliases.join(', '));
    setOpenEditDialog(true);
  };

  const handleOpenConfirmDialog = (item) => {
    setCurrentItem(item);
    setOpenConfirmDialog(true);
  };

  const handleAddAliases = async () => {
    const aliasesArray = aliases.split(',').map(alias => alias.trim());
    const requestData = {
      id: currentItem.id,
      aliases: aliasesArray,
    };
    try {
      const response = await fetchWithToken('/api/customers/oked/alias/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      if (response.ok) {
        fetchData(currentPage);
        setOpenAddDialog(false);
      } else {
        throw new Error('Failed to add aliases');
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleEditAliases = async () => {
    const aliasesArray = aliases.split(',').map(alias => alias.trim());
    const requestData = {
      id: currentItem.id,
      aliases: aliasesArray,
    };
    try {
      const response = await fetchWithToken('/api/customers/oked/alias/edit', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      if (response.ok) {
        fetchData(currentPage);
        setOpenEditDialog(false);
      } else {
        throw new Error('Failed to edit aliases');
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleAddForAll = async () => {
    const requestData = {
      id: currentItem.id,
    };
    try {
      const response = await fetchWithToken('/api/customers/alias/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      if (response.ok) {
        fetchData(currentPage); 
        setOpenConfirmDialog(false);
      } else {
        throw new Error('Failed to add aliases for all customers');
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center"><CircularProgress /></Box>;
  }

  if (error) {
    return <Typography color="error" align="center">{error}</Typography>;
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>OKED Наименование</TableCell>
              <TableCell>OKED Код</TableCell>
              <TableCell>Псевдонимы</TableCell>
              <TableCell>Добавить</TableCell>
              <TableCell>Редактировать</TableCell>
              <TableCell>Добавить для всех заказчиков</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell>{(currentPage - 1) * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.code}</TableCell>
                  <TableCell>{item.aliases.join(', ')}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ color: '#ffffff', backgroundColor: '#4298D7' }}
                      onClick={() => handleOpenAddDialog(item)}
                    >
                      Добавить
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ color: '#ffffff', backgroundColor: '#4CAF50' }}
                      onClick={() => handleOpenEditDialog(item)}
                    >
                      Редактировать
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ color: '#ffffff', backgroundColor: '#4CAF50' }}
                      onClick={() => handleOpenConfirmDialog(item)}
                    >
                      Добавить для всех заказчиков
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">Нет данных</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Paging
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Добавить Псевдонимы</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Псевдонимы"
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
            helperText="Введите псевдонимы через запятую"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Отмена</Button>
          <Button onClick={handleAddAliases} color="primary" variant="contained">
            Добавить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Редактировать Псевдонимы</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Псевдонимы"
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
            helperText="Введите псевдонимы через запятую"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Отмена</Button>
          <Button onClick={handleEditAliases} color="primary" variant="contained">
            Редактировать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Подтверждение</DialogTitle>
        <DialogContent>
          <Typography>Вы хотите добавить псевдонимы для всех заказчиков с таким же кодом OKED?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Отмена</Button>
          <Button onClick={handleAddForAll} color="primary" variant="contained">
            Да
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SearchCustomersTable;
